// frontend/src/components/HospitalExchangeForm.js
import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getDepartmentTermCapacities,
  getSecondResults,
  createOrUpdateHospitalExchangeRequest,
  updateUserFlag,
  getUserHospitalExchangePreferences,
} from '../services/api';
import ExchangePreferenceList from './ExchangePreferenceList';
import HospitalSidebar from './HospitalSidebar';
import LoadingSpinner from './LoadingSpinner';
import OnboardingTour from './OnboardingTour';
import { Button } from '../components/ui/button';
import { Calendar, Building2, HelpCircle } from 'lucide-react';

import '../styles/HospitalExchangeForm.css';
import '../styles/ExchangePreferenceList.css';
import '../styles/ExchangePreferenceRow.css';

// チュートリアルのステップ定義
const TOUR_STEPS = [
  {
    target: '[data-tour="exchange-form"]',
    title: '実習病院交換希望登録',
    content: 'このページでは、配属された病院の交換希望を登録できます。希望する病院を選んで申請してください。',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="current-assignments"]',
    title: '現行割当一覧',
    content: 'ここに現在の各タームの配属先が表示されています。交換を希望するタームを確認してください。',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="exchange-hospital-sidebar"]',
    title: '病院一覧',
    content: 'こちらから交換先として希望する病院・診療科を選んでドラッグしてください。',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour="exchange-preference-list"]',
    title: '交換希望リスト',
    content: 'ここで交換希望を設定します。まず「希望を追加する」ボタンを押して、交換希望を追加してください。',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-tour="exchange-add-button"]',
    title: '希望を追加',
    content: 'このボタンをクリックして交換希望を追加します。クリックすると新しい行が表示されます。',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-tour="exchange-row"]',
    title: '交換希望行',
    content: 'これが交換希望の1行です。各項目を設定して交換希望を完成させてください。',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="exchange-current-assignment"]',
    title: '現行割当',
    content: '選択した移動元タームの現在の配属先が表示されます。',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="exchange-source-term"]',
    title: '移動元ターム',
    content: '交換したい現在の配属のタームを選択します。',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="exchange-target-term"]',
    title: '移動先ターム',
    content: '交換先として希望するタームを選択します。同じタームなら同ターム内での病院変更、異なるタームなら別タームへの移動になります。',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="exchange-candidate"]',
    title: '希望候補',
    content: '右側の病院一覧から希望する病院・診療科をここにドラッグ＆ドロップしてください。',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="exchange-complement"]',
    title: '補完候補',
    content: '移動元と移動先のタームが異なる場合、交換が成立した場合に移動元タームの配属を補完するため、移動元タームの候補を3つ設定する必要があります。第1希望〜第3希望の順でドロップしてください。',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="exchange-accommodation"]',
    title: '宿泊希望',
    content: '交換先での宿泊を希望する場合はチェックを入れてください。',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="exchange-save-button"]',
    title: '登録を忘れずに！',
    content: '交換希望の設定が完了したら、必ず「登録する」ボタンを押して申請を完了してください。',
    placement: 'top',
    disableBeacon: true,
  },
];

// チュートリアル用の初期データ（移動元ターム1、移動先ターム2で補完候補が表示される状態）
const tutorialInitialPreference = {
  sourceTerm: 1,
  targetTerm: 2,
  candidate: null,
  complementCandidates: [null, null, null],
  accommodation: false,
};

const formatCurrentAssignments = (assignments) => {
  const formatted = {};
  for (let i = 1; i <= 7; i++) {
    const assignment = assignments.find((a) => a.term_id === i);
    formatted[i] = {
      term: i,
      institution_name: assignment ? assignment.institution_name : '未割り当て',
      department_name: assignment ? assignment.department_name : '',
      assignment_id: assignment ? assignment.assignment_id : null,
      department_term_capacity_id: assignment ? assignment.department_term_capacity_id : null,
    };
  }
  return formatted;
};

const HospitalExchangeForm = () => {
  const { user, loading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [hospitals, setHospitals] = useState({});
  const [currentAssignments, setCurrentAssignments] = useState({});
  // チュートリアル用：初期状態から1行追加された状態で開始
  const [exchangePreferences, setExchangePreferences] = useState([{ ...tutorialInitialPreference }]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [departmentTermCapacities, setDepartmentTermCapacities] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeItem, setActiveItem] = useState('');
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      if (!user) {
        setIsLoading(false);
        return;
      }
      try {
        const [
          deptTermCapResponse,
          secondAssignmentsResponse,
          hospitalExchangePreferencesResponse,
        ] = await Promise.all([
          getDepartmentTermCapacities(),
          getSecondResults(),
          getUserHospitalExchangePreferences(),
        ]);
        setDepartmentTermCapacities(deptTermCapResponse);
        const allTerms = [];
        for (let i = 1; i <= 7; i++) {
          const assignment = secondAssignmentsResponse.find((a) => a.term_id === i);
          if (assignment) {
            allTerms.push({
              term_id: i,
              institution_name: assignment.institution_name,
              department_name: assignment.department_name,
              assignment_id: assignment.assignment_id,
              department_term_capacity_id: assignment.department_term_capacity_id,
            });
          } else {
            allTerms.push({
              term_id: i,
              institution_name: '未割り当て',
              department_name: '',
              assignment_id: null,
              department_term_capacity_id: null,
            });
          }
        }
        setCurrentAssignments(formatCurrentAssignments(allTerms));
        const hospitalsData = deptTermCapResponse.reduce((acc, item) => {
          if (!acc[item.institution_id]) {
            acc[item.institution_id] = {
              name: item.institution_name,
              departments: [],
            };
          }
          acc[item.institution_id].departments.push(item);
          return acc;
        }, {});
        setHospitals(hospitalsData);
        
        // 既存の交換希望データを確認
        const existingPrefs = hospitalExchangePreferencesResponse?.[0]?.preferences;
        const hasExistingData = existingPrefs && Array.isArray(existingPrefs) && existingPrefs.length > 0;
        
        if (hasExistingData) {
          setExchangePreferences(existingPrefs);
        } else {
          // チュートリアル用：既存データがない場合は、補完候補が表示される状態で初期化
          // （移動元ターム1、移動先ターム2 で異なるため補完候補エリアが表示される）
          setExchangePreferences([{ ...tutorialInitialPreference }]);
        }
      } catch (err) {
        console.error('データ取得エラー:', err);
        setError('データの取得に失敗しました。');
      } finally {
        setIsLoading(false);
      }
    };
    if (user) {
      fetchData();
    }
  }, [user]);

  const handleSave = async () => {
    // バリデーション：preferences が存在する場合のみチェック
    if (exchangePreferences.length > 0) {
      for (let i = 0; i < exchangePreferences.length; i++) {
        const pref = exchangePreferences[i];
        if (!pref.candidate) {
          alert(`交換希望${i + 1} の希望候補が入力されていません。必ず候補を選択してください。`);
          return;
        }
        if (pref.sourceTerm !== pref.targetTerm) {
          // 補完候補が3件全て入力されていることをチェック
          if (
            !pref.complementCandidates ||
            pref.complementCandidates.length !== 3 ||
            pref.complementCandidates.some((comp) => comp === null)
          ) {
            alert(`交換希望${i + 1} の補完候補は必ず3件入力してください。`);
            return;
          }
        }
      }
    }
    setIsSaving(true);
    try {
      const firstAssignment =
        exchangePreferences.length > 0
          ? currentAssignments[exchangePreferences[0].sourceTerm]
          : currentAssignments[Object.keys(currentAssignments)[0]];
      if (!firstAssignment || !firstAssignment.assignment_id) {
        alert('現行割当情報がありません。');
        return;
      }
      // exchangePreferences をそのまま API に渡す
      await createOrUpdateHospitalExchangeRequest(
        firstAssignment.assignment_id,
        exchangePreferences
      );
      alert('交換希望が保存されました。');
      await updateUserFlag(user.uid, 'hospital_exchange_preference_status', true);
    } catch (error) {
      console.error('Failed to save exchange:', error);
      alert('交換希望の保存に失敗しました: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const onDragStart = (event) => {
    setActiveId(event.active.id);
    setActiveItem(event.active.data.current.value);
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveItem('');
    if (!over) return;
    if (over.id && over.id.startsWith('exchange-') && over.id.endsWith('-candidate')) {
      const parts = over.id.split('-');
      if (parts.length === 3) {
        const index = parseInt(parts[1], 10);
        if (!isNaN(index)) {
          setExchangePreferences((prev) => {
            const newPrefs = [...prev];
            newPrefs[index] = { ...newPrefs[index], candidate: active.data.current.value };
            return newPrefs;
          });
        }
      }
    } else if (over.id && over.id.startsWith('exchange-') && over.id.includes('complement')) {
      const parts = over.id.split('-');
      const rowIndex = parseInt(parts[1], 10);
      const compIndex = parseInt(parts[3], 10);
      if (!isNaN(rowIndex) && !isNaN(compIndex)) {
        setExchangePreferences((prev) => {
          const newPrefs = [...prev];
          const currentRow = { ...newPrefs[rowIndex] };
          const comps = currentRow.complementCandidates || [null, null, null];
          const complementCandidate = {
            ...active.data.current.value,
            term: currentRow.sourceTerm,
            preference_order: compIndex + 1,
          };
          comps[compIndex] = complementCandidate;
          currentRow.complementCandidates = comps;
          newPrefs[rowIndex] = currentRow;
          return newPrefs;
        });
      }
    }
  };

  const containerClass = isSidebarOpen
    ? 'hospital-exchange-form-container sidebar-open'
    : 'hospital-exchange-form-container sidebar-closed';

  if (loading || isLoading) {
    return <LoadingSpinner text="データを読み込んでいます..." />;
  }
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-background">
        <div className="text-accent text-xl">{error}</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" />;
  }

  return (
    <div className={containerClass}>
      {isSaving && (
        <div className="save-status-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="save-status-popup">
            <span className="save-status-spinner" aria-hidden="true"></span>
            <span>登録しています...</span>
          </div>
        </div>
      )}
      {/* オンボーディングツアー */}
      <OnboardingTour
        tourKey="hospital-exchange"
        steps={TOUR_STEPS}
        isReady={!loading && Object.keys(hospitals).length > 0}
      />
      
      <div className="hospital-exchange-form-wrapper" data-tour="exchange-form">
        <div className="form-header">
          <h2 className="form-title">実習病院交換希望登録</h2>
          <button
            type="button"
            onClick={() => {
              try {
                localStorage.removeItem('tour-completed-hospital-exchange');
              } catch {}
              window['hospital-exchangeTour']?.startTour?.();
            }}
            title="操作ガイドを再表示"
            className="tour-button"
          >
            <HelpCircle size={18} />
            <span>操作ガイド</span>
          </button>
        </div>
        
        {/* 現行割当一覧 - 横一列グリッド表示 */}
        <div className="current-assignments" data-tour="current-assignments">
          <h3 className="section-title">
            <Calendar size={20} style={{ color: '#1e40af' }} />
            現行割当一覧
          </h3>
          <div className="current-assignment-grid">
            {Object.entries(currentAssignments).map(([term, info]) => (
              <div key={term} className="current-assignment-item">
                <div className="term-label">第{term}ターム</div>
                <div className="assignment-info">
                  <span className="hospital-name">{info.institution_name}</span>
                  {info.department_name && (
                    <span className="department-name">{info.department_name}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <DndContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="exchange-preference-list-container" data-tour="exchange-preference-list">
            <h3 className="section-title">
              <Building2 size={20} style={{ color: '#1e40af' }} />
              交換希望リスト
            </h3>
            <ExchangePreferenceList
              value={exchangePreferences}
              onChange={setExchangePreferences}
              currentAssignments={currentAssignments}
            />
          </div>
          <div className="save-button-container" data-tour="exchange-save-button">
            <Button onClick={handleSave} className="save-button" disabled={isSaving}>
              {isSaving ? '登録しています...' : '登録する'}
            </Button>
          </div>
          <HospitalSidebar
            hospitals={hospitals}
            isOpen={isSidebarOpen}
            toggleSidebar={toggleSidebar}
            departmentTermCapacities={departmentTermCapacities}
            dataTour="exchange-hospital-sidebar"
          />
          {createPortal(
            <DragOverlay>
              {activeId ? (
                <div className="drag-overlay" style={{ transform: undefined }}>
                  {typeof activeItem === 'object' ? activeItem.display_name : activeItem}
                </div>
              ) : null}
            </DragOverlay>,
            document.body
          )}
        </DndContext>
      </div>
    </div>
  );
};

export default HospitalExchangeForm;
